import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, ShieldCheck, Search, Save, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PermissionEditor, { permCellStatus } from "@/components/permissions/PermissionEditor";

const ROLE_LABEL = { segreteria: "Segreteria", raccolta_fondi: "Raccolta fondi", economato: "Economato" };
const MANAGED_ROLES = Object.keys(ROLE_LABEL);

function StatusBadge({ s }) {
  if (s.full) return <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px] rounded-full">Tutto</Badge>;
  if (s.count > 0) return <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] rounded-full">{s.count}/{s.total}</Badge>;
  return <Badge className="bg-stone-100 text-stone-500 border border-stone-200 text-[10px] rounded-full">—</Badge>;
}

function EditDialog({ target, modules, onClose }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState([]);
  const [inherit, setInherit] = useState(false);
  React.useEffect(() => {
    if (!target) return;
    setSelected(target.permissions || []);
    setInherit(!!target.inheritable && target.inherited);
  }, [target]);
  const save = useMutation({
    mutationFn: async () => {
      if (target.kind === "role") return (await api.put(`/users/permissions/roles/${target.role}`, { permissions: selected })).data;
      return (await api.patch(`/users/${target.id}/permissions`, { permissions: inherit ? null : selected })).data;
    },
    onSuccess: () => {
      toast.success("Permessi aggiornati. L'utente li vedrà al prossimo caricamento della pagina.");
      ["users-all-perm", "role-templates"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="permessi-dialog">
        <DialogHeader>
          <DialogTitle>Permessi · {target?.title}</DialogTitle>
          <DialogDescription>
            {target?.kind === "role"
              ? "Sezioni visibili di base per tutti gli utenti di questo ruolo che non hanno permessi personalizzati."
              : "Scegli se l'utente eredita i permessi del suo ruolo o se ha permessi personalizzati."}
          </DialogDescription>
        </DialogHeader>
        {target?.kind === "user" && (
          <label className="flex items-center gap-3 p-3 rounded-lg bg-stone-50 border border-stone-200 text-sm cursor-pointer">
            <Switch checked={inherit} onCheckedChange={(v) => { setInherit(v); if (v) setSelected(target.rolePermissions); }} data-testid="permessi-inherit-switch"/>
            <span>Eredita i permessi del ruolo <b>{ROLE_LABEL[target.role]}</b></span>
          </label>
        )}
        <PermissionEditor modules={modules} selected={inherit ? target?.rolePermissions || [] : selected} onChange={setSelected} disabled={inherit} testPrefix="permessi"/>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>Annulla</Button>
          <Button data-testid="permessi-save" className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" onClick={() => save.mutate()} disabled={save.isPending}>
            <Save size={14} className="mr-1.5"/> Salva permessi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PermTable({ rows, modules, onEdit, emptyText }) {
  if (!rows.length) return <div className="py-14 text-center text-stone-400 text-sm"><Users size={28} className="mx-auto mb-2 opacity-50"/>{emptyText}</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-stone-50/60 text-[10px] uppercase tracking-widest text-stone-500">
            <th className="text-left px-4 py-3 font-medium min-w-[220px]">Nome</th>
            {modules.map((m) => <th key={m.key} className="text-center px-3 py-3 font-medium">{m.label}</th>)}
            <th className="text-right px-4 py-3 font-medium">Azione</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {rows.map((r) => (
            <tr key={r.key} data-testid={`permessi-row-${r.key}`} className="hover:bg-stone-50">
              <td className="px-4 py-3">
                <div className="font-medium">{r.title}</div>
                <div className="text-[11px] text-stone-500">{r.subtitle}</div>
              </td>
              {modules.map((m) => <td key={m.key} className="text-center px-3 py-3"><StatusBadge s={permCellStatus(r.permissions, m)}/></td>)}
              <td className="px-4 py-3 text-right">
                <Button data-testid={`permessi-edit-${r.key}`} variant="outline" size="sm" className="rounded-full" onClick={() => onEdit(r)}>Modifica</Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Permessi() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [tab, setTab] = useState("roles");
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);

  const { data: catalog } = useQuery({ queryKey: ["permissions-catalog"], queryFn: async () => (await api.get("/users/permissions/catalog")).data, enabled: isAdmin });
  const { data: roles = [] } = useQuery({ queryKey: ["role-templates"], queryFn: async () => (await api.get("/users/permissions/roles")).data, enabled: isAdmin });
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users-all-perm"], queryFn: async () => (await api.get("/users")).data, enabled: isAdmin });
  const modules = catalog?.modules || [];
  const roleMap = Object.fromEntries(roles.map((r) => [r.role, r.permissions]));

  const roleRows = roles.map((r) => ({
    key: `role-${r.role}`, kind: "role", role: r.role, title: r.label, permissions: r.permissions,
    subtitle: `${users.filter((u) => u.role === r.role && u.permissions_inherited).length} utenti ereditano${r.is_default ? " · impostazione predefinita" : ""}`,
  }));
  const userRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => MANAGED_ROLES.includes(u.role))
      .filter((u) => !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q))
      .map((u) => ({
        key: u.id, id: u.id, kind: "user", role: u.role, title: u.name, inheritable: true,
        inherited: u.permissions_inherited, permissions: u.effective_permissions || [],
        subtitle: `${u.email} · ${ROLE_LABEL[u.role]} · ${u.permissions_inherited ? "eredita dal ruolo" : "personalizzati"}`,
      }));
  }, [users, search]);

  if (!isAdmin) {
    return (
      <div className="p-10 max-w-lg mx-auto text-center" data-testid="permessi-access-denied">
        <ShieldCheck size={40} className="mx-auto text-stone-400 mb-3"/>
        <div className="font-medium">Accesso riservato</div>
        <div className="text-sm text-stone-500 mt-1">Solo l'admin può gestire i permessi utente.</div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <Link to="/impostazioni" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4"><ArrowLeft size={13}/> Torna alle impostazioni</Link>
      <PageHeader title="Permessi utenti" subtitle="Admin e Direzione vedono sempre tutto. Docenti e studenti usano i loro portali dedicati."/>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-4">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="roles" data-testid="permessi-tab-roles">Per ruolo</TabsTrigger>
            <TabsTrigger value="users" data-testid="permessi-tab-users">Per utente</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "users" && (
          <div className="relative sm:w-72">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input data-testid="permessi-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca utente…" className="pl-9"/>
          </div>
        )}
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none">
        {isLoading ? <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
          : tab === "roles"
            ? <PermTable rows={roleRows} modules={modules} onEdit={setEditTarget} emptyText="Nessun ruolo configurabile."/>
            : <PermTable rows={userRows} modules={modules} onEdit={(r) => setEditTarget({ ...r, rolePermissions: roleMap[r.role] || [] })} emptyText="Nessun utente Segreteria, Raccolta fondi o Economato."/>}
      </Card>
      <EditDialog target={editTarget} modules={modules} onClose={() => setEditTarget(null)}/>
    </div>
  );
}
